import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { Link } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import httpClient from "../../lib/httpClient";
import { formatDateValue, getCompanyCodeName } from "../util/tableUtils";
import { DisplayField } from "../util/DisplayField";
import { useClientConfig } from "../../context/clientConfigContext";

const useStyles = makeStyles(() => ({
  copyableLink: {
    userSelect: "text",
  },
  typographyWithHover: {
    marginLeft: "4px",
  },
}));

export const AgriculturePolicyDescription = (props) => {
  const agriculturePolicy = props.policy;
  const { t } = useTranslation();
  const config = useClientConfig();

  const [location, setLocation] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const classes = useStyles();

  useEffect(() => {
    if (agriculturePolicy.policyNumber) {
      httpClient()
        .get(`/agriculturePolicies/${agriculturePolicy.policyNumber}/geometry`)
        .then((res) => res.data)
        .then(async (data) => {
          if (!data.policyGeo || data.policyGeo.length === 0) {
            console.log("No location data found for policy", agriculturePolicy.policyNumber);
            return;
          }

          const latNumeric = data.policyGeo[0].centerLat;
          const lonNumeric = data.policyGeo[0].centerLon;
          if (latNumeric && lonNumeric) {
            const lat = latNumeric.toFixed(5);
            const lon = lonNumeric.toFixed(5);
            setLocation(`${lat}, ${lon}`);
            const zoomLevel = 19;
            setGoogleMapsUrl(
              `https://www.google.com/maps/@${lat},${lon},${zoomLevel}z/data=!3m1!1e3?hl=en&q=${lat},${lon}(My+Location)`,
            );
          }
        });
    }
  }, [agriculturePolicy.policyNumber]);

  const goToAgriPolicyPage = (policyNumber) => {
    const wnd = window.open(`#/policiesagriculture/${policyNumber}`, "_blank");
    wnd.onload = function () {
      wnd.document.title = `${policyNumber}`;
    };
  };

  return (
    <Box p={1}>
      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <Typography variant="body2" display="block" component={"span"}>
            <Box sx={{ fontWeight: 600 }}>{t("agriculture.policyNo")}</Box>
          </Typography>
          <Link
            component="button"
            variant="body2"
            className={classes.copyableLink}
            onClick={() => {
              goToAgriPolicyPage(agriculturePolicy.policyNumber);
            }}
          >
            {agriculturePolicy.policyNumber}
          </Link>
        </Box>

        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("company")}
            value={getCompanyCodeName(agriculturePolicy.companyCode, config.agriCompanyCodesMap)}
          />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.year")} value={agriculturePolicy.referenceDate} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.season")} value={agriculturePolicy.seasonDescription} />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("agency")}
            value={`${agriculturePolicy.agencyCode}  ${agriculturePolicy.agencyDescription}`}
          />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("subagencyCode")} value={agriculturePolicy.subagencyCode} />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("agriculture.municipality") + "/" + t("agriculture.sezione")}
            value={
              agriculturePolicy.addressCity +
              (agriculturePolicy.cadastralSectionForFirstPartita
                ? " / " + agriculturePolicy.cadastralSectionForFirstPartita
                : "")
            }
          />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.province")} value={agriculturePolicy.addressProvince} />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.region")} value={agriculturePolicy.addressRegion} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("agriculture.notificationDate")}
            value={formatDateValue(agriculturePolicy.dateNotification)}
          />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.specie")} value={agriculturePolicy.plantSpecies} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("agriculture.macrospecie")} value={agriculturePolicy.plantMacropecies} />
        </Box>
      </Box>

      <Box display="flex" mt="1em">
        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("agriculture.location")}
            value={location ? location : t("agriculture.locationUnknown")}
          />
        </Box>
        {location && (
          <Box flex={1} mr="0.5em">
            <Link href={googleMapsUrl} target="_blank" rel="noopener">
              Google map
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
};
